import { themeVars } from '@synoptic/ui-kit/theme/vars.ts';
import { borderRadius, space } from '@synoptic/ui-kit/tokens.js';
import { typography } from '@synoptic/ui-kit/typography.js';
import { globalStyle, style } from '@vanilla-extract/css';

export const streamsList = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s8,
  marginTop: space.s12,
});

export const streamRow = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s8,
  width: '100%',
  minHeight: 40,
  padding: `0 ${space.s12}px`,
  borderRadius: borderRadius.r8,
  border: `1px solid ${themeVars.container.border.default}`,
  backgroundColor: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',

  ':hover': {
    backgroundColor: themeVars.controls.background.hover,
  },
});

export const streamName = style([
  {
    color: themeVars.container.foreground.primary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flexShrink: 0,
    maxWidth: '40%',
  },
  typography.labelMd,
]);

export const chipBadge = style([
  typography.labelSm,
  {
    height: 20,
    padding: `0 ${space.s8}px`,
    gap: space.s4,
  },
]);

export const chipBadgeText = style({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: 96,
});

export const countBadge = style([
  chipBadge,
  {
    fontVariantNumeric: 'tabular-nums',
    flexShrink: 0,
  },
]);

export const noShrinkChipBadge = style([
  chipBadge,
  {
    flexShrink: 0,
  },
]);

export const headerSummary = style([
  {
    marginLeft: 'auto',
    color: themeVars.container.foreground.tertiary,
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  typography.labelMd,
]);

export const rowRight = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s4,
  marginLeft: 'auto',
  minWidth: 0,
});

export const tooltipTargets = style({
  display: 'flex',
  flexWrap: 'wrap',
  gap: space.s4,
  maxWidth: 260,
});

export const tooltipTargetChipText = style([
  chipBadgeText,
  {
    maxWidth: 220,
  },
]);

export const chevron = style({
  color: themeVars.container.foreground.tertiary,
  flexShrink: 0,
  transitionProperty: 'transform',
  transitionDuration: '0.15s',

  selectors: {
    [`${streamRow}[data-state="open"] &`]: {
      transform: 'rotate(180deg)',
    },
  },
});

export const targetsPopover = style({
  width: 'var(--radix-popover-trigger-width)',
  padding: 0,
  overflow: 'hidden',
  border: `1px solid ${themeVars.container.border.default}`,
});

export const popoverHeader = style([
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.s8,
    padding: `${space.s8}px ${space.s12}px`,
    color: themeVars.container.foreground.secondary,
  },
  typography.labelSm,
]);

export const targetsList = style({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 240,
  overflowY: 'auto',
});

export const targetItem = style([
  {
    display: 'flex',
    alignItems: 'center',
    gap: space.s8,
    padding: `${space.s6}px ${space.s12}px`,
    cursor: 'pointer',
    color: themeVars.container.foreground.primary,

    ':hover': {
      backgroundColor: themeVars.controls.background.hover,
    },
  },
  typography.bodySm,
]);

export const targetItemText = style({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const emptySearchResult = style([
  {
    padding: `${space.s8}px ${space.s12}px`,
    color: themeVars.container.foreground.tertiary,
  },
  typography.bodySm,
]);

export const popoverFooter = style([
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.s8,
    padding: `${space.s8}px ${space.s12}px`,
    borderTop: `1px solid ${themeVars.container.border.default}`,
    color: themeVars.container.foreground.secondary,
  },
  typography.labelSm,
]);

export const streamField = style({
  display: 'flex',
  flexDirection: 'column',
  gap: space.s8,
});

// Typed targets join the list, so the input belongs with the ones that are
// in it. The tag row is a flex container and the input is its last child, so
// everything excluded is ordered after it instead.
export const tagRow = style({});

globalStyle(`${tagRow} > input`, {
  order: 1,
});

const AFTER_INPUT = { order: 2 } as const;

// The two states share the tag shape the manage-targets modal uses; only
// the fill says whether this list takes the target, so an excluded one is
// left exactly as that tag looks everywhere else.
export const includedTag = style({
  border: 0,
  cursor: 'pointer',
  color: themeVars.container.foreground.primary,
  selectors: {
    // The shared tag sets `background`, which would otherwise win over a
    // `background-color` declared here.
    '&:is(&)': {
      padding: `0 ${space.s12}px`,
      background: `color-mix(in oklab, ${themeVars.container.foreground.brand} 36%, transparent)`,
    },
  },
});

export const tagLabelButton = style({
  border: 0,
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  font: 'inherit',
  color: 'inherit',
});

// Full-width in the wrap container, so what follows starts on its own line.
export const excludedTag = style(AFTER_INPUT);

export const sectionBreak = style([
  typography.labelSm,
  {
    ...AFTER_INPUT,
    flexBasis: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: space.s8,
    marginTop: space.s4,
    color: themeVars.container.foreground.tertiary,
    '::after': {
      content: '""',
      flex: 1,
      height: 1,
      backgroundColor: themeVars.container.border.default,
    },
  },
]);

export const emptyExcluded = style([
  typography.bodySm,
  {
    ...AFTER_INPUT,
    flexBasis: '100%',
    color: themeVars.container.foreground.tertiary,
  },
]);

export const targetsHint = style([
  typography.bodySm,
  {
    margin: 0,
    color: themeVars.container.foreground.tertiary,
  },
]);

export const targetsCount = style({
  display: 'flex',
  alignItems: 'center',
  gap: space.s8,
});

export const targetsFoot = style([
  typography.bodySm,
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: space.s12,
    color: themeVars.container.foreground.secondary,
  },
]);

export const removalAlert = style([
  typography.bodySm,
  {
    display: 'flex',
    alignItems: 'center',
    gap: space.s12,
    color: themeVars.color.text.alert.critical,
  },
]);
